<div class="settings-shell">
  <nav class="settings-nav" aria-label="Settings sections">
    @for (section of sections; track section.id) {
      @if (section.id !== 'workspace' || (users$ | async)) {
        @if (section.children) {
          <div class="settings-nav__group">
            <button
              type="button"
              class="settings-nav__item settings-nav__item--group"
              [class.is-active]="activeSection === section.id"
              (click)="selectSection(section.id)"
            >{{ section.label }}</button>
            @for (child of section.children; track child.id) {
              <button
                type="button"
                class="settings-nav__item settings-nav__item--child"
                [class.is-active]="activeSection === child.id"
                (click)="selectSection(child.id)"
              >{{ child.label }}</button>
            }
          </div>
        } @else {
          <button
            type="button"
            class="settings-nav__item"
            [class.is-active]="activeSection === section.id"
            (click)="selectSection(section.id)"
          >{{ section.label }}</button>
        }
      }
    }
  </nav>

  <div class="settings-content">
    @if (activeSection === 'general') {
    <div class="settings-section">
      <section class="settings-tile">
        <h2>Settings</h2>

        <div class="settings-stack">
          <label class="settings-check">
            <input
              type="checkbox"
              [(ngModel)]="optionApi"
              (change)="valueChange('optionApi')"
              />
            <span>Use new API to access tenant and environment. [Experimental 2025]</span>
            <span
              class="settings-help"
              title="Uses the newer Power Platform API path for tenant and environment discovery. Toggle off if tenant/environment loading behaves unexpectedly."
              aria-label="Uses the newer Power Platform API path for tenant and environment discovery."
            >?</span>
          </label>

          <label class="settings-check">
            <input
              type="checkbox"
              [(ngModel)]="optionFlowBatchingApi"
              (change)="valueChange('optionFlowBatchingApi')"
              />
            <span>Enable FlowStudio batching API. [Default On]</span>
            <span
              class="settings-help"
              title="Uses Microsoft's Power Automate batch API from the browser for per-flow detail, owner, and run reads. FlowStudio no longer uses the older server /api/flows batching path for scans."
              aria-label="Batching API help"
            >?</span>
          </label>

          <label class="settings-field settings-field--inline">
            <span class="settings-label-with-help">
              Last Viewed (default start page)
              <span
                class="settings-help"
                title="Choose the page FlowStudio opens after sign-in. Settings itself is not saved as last viewed."
                aria-label="Choose the page FlowStudio opens after sign-in."
              >?</span>
            </span>
            <kendo-dropdownlist
              [data]="defaultRouteOptions"
              [textField]="'text'"
              [valueField]="'value'"
              [(ngModel)]="optionDefaultRoute"
              (valueChange)="valueChange('optionDefaultRoute')"
            ></kendo-dropdownlist>
          </label>

          <label class="settings-field settings-field--inline">
            <span class="settings-label-with-help">
              Auto-refresh every
              <span
                class="settings-help"
                title="Controls how long an environment scan is considered fresh before FlowStudio automatically refreshes it. Minimum is 1 hour."
                aria-label="Controls how long an environment scan is considered fresh before FlowStudio automatically refreshes it."
              >?</span>
            </span>
            <span class="settings-number-wrap">
              <input
                type="number"
                min="1"
                step="1"
                [(ngModel)]="optionAutoRefreshHours"
                (change)="valueChange('optionAutoRefreshHours')"
                (blur)="valueChange('optionAutoRefreshHours')"
                />
              <span>hour(s)</span>
            </span>
          </label>
        </div>
      </section>
    </div>
    }

    @if (activeSection === 'flows-grid') {
    <div class="settings-section">
      <section class="settings-tile">
        <h2>
          Grid Columns
          <span
            class="settings-help"
            title="Choose which columns are visible on the main Flows grid. # Actions is default off because Flow API changes mean the flow summary no longer reliably includes all actions."
            aria-label="Choose which columns are visible on the main Flows grid."
          >?</span>
        </h2>

        <div class="settings-check-grid">
          @for (col of allColumns; track col) {
            @if (col.defaultOn) {
              <label class="settings-check">
                <input
                  type="checkbox"
                  [checked]="selectedColumns.includes(col.key)"
                  (change)="toggleColumn(col.key)"
                  />
                <span>{{ col.label }}</span>
                @if (col.help) {
                  <span
                    class="settings-help"
                    [title]="col.help"
                    [attr.aria-label]="col.help"
                  >?</span>
                }
              </label>
            }
          }
        </div>

        <div class="settings-check-grid">
          @for (col of allColumns; track col) {
            @if (!col.defaultOn) {
              <label class="settings-check">
                <input
                  type="checkbox"
                  [checked]="selectedColumns.includes(col.key)"
                  (change)="toggleColumn(col.key)"
                  />
                <span>{{ col.label }}</span>
                @if (col.help) {
                  <span
                    class="settings-help"
                    [title]="col.help"
                    [attr.aria-label]="col.help"
                  >?</span>
                }
              </label>
            }
          }
        </div>
      </section>

      <section class="settings-tile">
        <h2>
          Flow Hover Links
          <span
            class="settings-help"
            title="Choose which external portal edit links appear when hovering over a flow name. All links are visible by default."
            aria-label="Choose which external portal edit links appear when hovering over a flow name."
          >?</span>
        </h2>

        <div class="settings-check-grid">
          @for (link of allFlowLinks; track link) {
            <label class="settings-check">
              <input
                type="checkbox"
                [checked]="visibleFlowLinks.includes(link.key)"
                (change)="toggleFlowLink(link.key)"
                />
              <span>{{ link.label }}</span>
              @if (link.help) {
                <span
                  class="settings-help"
                  [title]="link.help"
                  [attr.aria-label]="link.help"
                >?</span>
              }
            </label>
          }
        </div>
      </section>
    </div>
    }

    @if (activeSection === 'run-history') {
    <div class="settings-section">
      <section class="settings-tile">
        <h2>
          Flow Run Refresh Groups
          <span
            class="settings-help"
            title="Choose which groups of active flows are included when FlowStudio refreshes run history. All groups are on by default."
            aria-label="Choose which groups of active flows are included when FlowStudio refreshes run history."
          >?</span>
        </h2>

        <div class="settings-check-grid">
          @for (group of allRunScanGroups; track group) {
            <label class="settings-check">
              <input
                type="checkbox"
                [checked]="selectedRunScanGroups.includes(group.key)"
                (change)="toggleRunScanGroup(group.key)"
                />
              <span>{{ group.label }}</span>
              <span
                class="settings-help"
                [title]="group.help"
                [attr.aria-label]="group.help"
              >?</span>
            </label>
          }
        </div>
      </section>
    </div>
    }

    @if (activeSection === 'tools') {
    <div class="settings-section">
      <section class="settings-tile">
        <h2>
          Deprecated Tools
          <span
            class="settings-help"
            title="Show or hide retired tools. Edit in Designer opens the older Flow designer in a popup and is retired. New submenu links to old templates and is retired."
            aria-label="Show or hide older tools and menu entries while they are being phased out."
          >?</span>
        </h2>

        <div class="settings-check-grid">
          @for (tool of allDeprecatedTools; track tool) {
            @if (tool.defaultOn) {
              <label class="settings-check">
                <input
                  type="checkbox"
                  [checked]="visibleDeprecatedTools.includes(tool.key)"
                  (change)="toggleDeprecatedTool(tool.key)"
                  />
                <span>{{ tool.label }}</span>
                @if (tool.help) {
                  <span
                    class="settings-help"
                    [title]="tool.help"
                    [attr.aria-label]="tool.help"
                  >?</span>
                }
              </label>
            }
          }
        </div>

        <div class="settings-check-grid">
          @for (tool of allDeprecatedTools; track tool) {
            @if (!tool.defaultOn) {
              <label class="settings-check">
                <input
                  type="checkbox"
                  [checked]="visibleDeprecatedTools.includes(tool.key)"
                  (change)="toggleDeprecatedTool(tool.key)"
                  />
                <span>{{ tool.label }}</span>
                @if (tool.help || tool.key === 'newSubmenu') {
                  <span
                    class="settings-help"
                    [title]="tool.help || 'Links to old templates, so this submenu has been retired.'"
                    [attr.aria-label]="tool.help || 'Links to old templates, so this submenu has been retired.'"
                  >?</span>
                }
              </label>
            }
          }
        </div>
      </section>
    </div>
    }

    @if (activeSection === 'workspace' && (users$ | async)) {
      <div class="settings-section">
        <section class="settings-tile">
          <h2>Workspace</h2>
          <app-user-license></app-user-license>
        </section>
      </div>
    }

    @if (activeSection === 'account') {
    <div class="settings-section">
      <section class="settings-tile">
        <h2>Account</h2>

        <div class="settings-account">
          <div><strong>User</strong>: {{ (user$ | async)?.username }}</div>
          <div><strong>License</strong>: {{ (membership$ | async)?.status }}</div>

          <p>
            Access Stripe customer billing portal to change billing details or download previous invoice and receipts.
          </p>

          @if ({ url: paymentService.portal$ | async }; as portal) {
            <a
              class="settings-button settings-button--link"
              target="_blank"
              [href]="portal.url"
              title="Stripe customer billing portal"
              [class.settings-button--disabled]="!portal.url"
              >
              <fa-icon icon="address-card"></fa-icon>
              <span>Customer Billing Portal</span>
            </a>
          }

          @if (workspace$ | async; as ws) {
            <div><strong>Workspace</strong>: {{ ws.title }}</div>
            <div><strong>Seats Pro</strong>: {{ ws.seats_pro }}</div>
            <div><strong>Seats Teams</strong>: {{ ws.seats_teams }}</div>
          }

          <h3 class="settings-account__subhead">
            Contact Email
            <span
              class="settings-help"
              title="A separate email for receipts, alerts, and important updates. Useful when your sign-in account doesn't have a mailbox."
              aria-label="Contact email is a separate email for receipts and alerts."
            >?</span>
          </h3>
          <app-contact-email-section></app-contact-email-section>
        </div>
      </section>
    </div>
    }
  </div>
</div>
